import type { Tables } from "@/integrations/supabase/types";
import type { ServiceContext } from "@prolarus/shared";
import { apiDelete, apiGet, apiPost, ApiError } from "@/lib/apiClient";

export type ComplianceCheck = Tables<"compliance_checks">;

export interface ComplianceDashboardRow {
  invoice_id: string;
  invoice_number: string;
  vendor_name: string;
  mismatch_count: number;
  total_overcharge: number;
}

function toError(err: unknown): Error {
  if (err instanceof ApiError) return err;
  if (err instanceof Error) return err;
  return new Error(String(err));
}

export const complianceCheckService = {
  async runForInvoices(invoiceIds: string[], _context: ServiceContext) {
    if (invoiceIds.length === 0) return { data: [] as ComplianceCheck[], error: null };
    try {
      const result = await apiPost<{ data: ComplianceCheck[] }>("/api/compliance/run", {
        invoice_ids: invoiceIds,
      });
      return { data: result.data, error: null };
    } catch (err) {
      return { data: [] as ComplianceCheck[], error: toError(err) };
    }
  },

  async runForInvoice(invoiceId: string, context: ServiceContext) {
    return complianceCheckService.runForInvoices([invoiceId], context);
  },

  async listByInvoice(invoiceId: string) {
    try {
      const result = await apiGet<{ data: ComplianceCheck[] }>(
        `/api/compliance/by-invoice/${encodeURIComponent(invoiceId)}`,
      );
      return { data: result.data, error: null };
    } catch (err) {
      return { data: [] as ComplianceCheck[], error: toError(err) };
    }
  },

  async listByInvoices(invoiceIds: string[]) {
    if (invoiceIds.length === 0) return { data: [] as ComplianceCheck[], error: null };
    try {
      const result = await apiPost<{ data: ComplianceCheck[] }>("/api/compliance/by-invoices", {
        invoice_ids: invoiceIds,
      });
      return { data: result.data, error: null };
    } catch (err) {
      return { data: [] as ComplianceCheck[], error: toError(err) };
    }
  },

  async deleteByInvoice(invoiceId: string) {
    try {
      const result = await apiDelete<{ success: boolean; changesLogged: boolean }>(
        `/api/compliance/by-invoice/${encodeURIComponent(invoiceId)}`,
      );
      return { success: result.success, error: null, changesLogged: !!result.changesLogged };
    } catch (err) {
      return { success: false, error: toError(err), changesLogged: false };
    }
  },

  async getDashboardSummary(_companyId: string, sinceDate?: string) {
    try {
      const url = sinceDate
        ? `/api/compliance/dashboard?sinceDate=${encodeURIComponent(sinceDate)}`
        : "/api/compliance/dashboard";
      const result = await apiGet<{ data: ComplianceDashboardRow[] }>(url);
      return { data: result.data, error: null };
    } catch (err) {
      return { data: [] as ComplianceDashboardRow[], error: toError(err) };
    }
  },
};
